// @mui
import { alpha } from '@mui/material/styles';
import { Box, Card, Chip, Container, Grid, IconButton, Stack, Typography, Button } from '@mui/material';
import CopyIcon from '@mui/icons-material/CopyAll';
// components
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Page from '../components/Page';
import AlertForPremium from '../ui-component/AlertForPremium';
import { copyText } from '../services/helper';
import { useLanguage } from '../context/LanguageContext';
import * as actions from '../store/actions';
import Iconify from '../components/Iconify';
import PageWorkingAlert from '../ui-component/PageWorkingAlert';
// ----------------------------------------------------------------------

export default function DashboardAppStarted() {
  const voiceText = false;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hl, t } = useLanguage();
  const showPay = false;
  const startedTitle = t('started.title'); // Listening..
  const note = t('Note'); // Note
  const startedListeningFor = t('listenFor'); // Listening for
  const and = t('and'); // And
  const typingInto = t('typingInto'); // Typing into
  const startedNote = t('started.note'); // Keep this page open for typing on other pages. This is compulsory in V3 Extensions.
  const errorMsg = null;
  const preferredLangs = useSelector((state) => state.pLanguage.data);
  const doTranslate = useSelector((state) => state.pLanguage.translate);
  const targetLang = useSelector((state) => state.pLanguage.targetLanguage);
  const startedLabel = doTranslate
    ? `${startedListeningFor} <b>${hl.name}</b> ${and} ${typingInto} <b>${targetLang}</b>...`
    : `${startedListeningFor} <b>${hl.name}</b>...`;
  const waveformBars = [34, 52, 28, 74, 48, 86, 62, 38, 78, 54, 92, 44, 68, 30, 58, 80, 46, 66];

  const onCancel = () => {
    dispatch({ type: actions.VOICE_TYPING_STOP });
    navigate('/dashboard/app');
  };

  useEffect(() => {
    dispatch({ type: actions.MIC_NOTE_SHOW });
    dispatch({ type: actions.VOICE_TYPING_START });
  }, [dispatch]);

  return (
    <>
      <AlertForPremium open={showPay} onClose={onCancel} error={errorMsg} />
      <Page title={startedTitle}>
        <PageWorkingAlert />
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={12} md={8}>
                <Card
                  sx={{
                    minHeight: 520,
                    p: { xs: 3, md: 5 },
                    color: 'common.white',
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'linear-gradient(135deg, #171717 0%, #23221F 55%, #0F3D28 100%)',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 'auto -15% -35% auto',
                      width: 380,
                      height: 380,
                      borderRadius: '50%',
                      bgcolor: alpha('#20B26B', 0.2),
                    }}
                  />
                  <Stack spacing={4} sx={{ position: 'relative', height: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Chip
                        label="REC Voice Typing"
                        sx={{ bgcolor: 'error.main', color: 'common.white', fontWeight: 800 }}
                      />
                      <Button
                        variant="contained"
                        color="error"
                        onClick={onCancel}
                        startIcon={<Iconify icon="eva:stop-circle-outline" />}
                      >
                        Stop
                      </Button>
                    </Stack>

                    <Box>
                      <Typography variant="h2" sx={{ color: 'common.white', lineHeight: 1.08, mb: 2 }}>
                        Listening now.
                        <br />
                        Speak naturally.
                      </Typography>
                      <Typography
                        sx={{ color: alpha('#FFFFFF', 0.72), maxWidth: 640 }}
                        dangerouslySetInnerHTML={{ __html: startedLabel }}
                      />
                    </Box>

                    <Card sx={{ p: 2.5, bgcolor: alpha('#FFFFFF', 0.08), border: `1px solid ${alpha('#FFFFFF', 0.12)}`, boxShadow: 0 }}>
                      <Stack spacing={2.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" sx={{ color: 'common.white' }}>
                            Live microphone
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.62) }}>
                            Keep this tab open
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ height: 120 }}>
                          {waveformBars.map((height, index) => (
                            <Box
                              key={index}
                              sx={{
                                width: 9,
                                height,
                                borderRadius: 99,
                                bgcolor: 'secondary.main',
                                boxShadow: '0 0 24px rgba(32, 178, 107, 0.28)',
                              }}
                            />
                          ))}
                        </Stack>
                        <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.62) }}>
                          Dictation is active across browser text fields. Use your normal commands for punctuation, new lines, and replacements.
                        </Typography>
                      </Stack>
                    </Card>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Card sx={{ p: 2.5, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                      Speak
                    </Typography>
                    <Typography variant="h5">{hl?.name}</Typography>
                  </Card>
                  <Card sx={{ p: 2.5, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                      Output
                    </Typography>
                    <Typography variant="h5">{doTranslate && targetLang ? targetLang : 'Same language'}</Typography>
                  </Card>
                  <Card sx={{ p: 2.5, flexGrow: 1, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Stack spacing={1.5}>
                      <Typography variant="h5">Quick languages</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Jump to a saved language without leaving the listening flow.
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {preferredLangs.map((lang) => (
                          <Chip
                            key={lang}
                            label={lang}
                            component="a"
                            href={`?hl=${lang}`}
                            clickable
                            sx={{ bgcolor: 'background.neutral', fontWeight: 600 }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Card>
                </Stack>
              </Grid>
            </Grid>

            {voiceText && (
              <Alert
                icon={<Iconify icon="emojione-monotone:studio-microphone" />}
                severity="info"
                action={
                  <IconButton
                    aria-label="copy voice text"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      copyText(voiceText);
                    }}
                  >
                    <CopyIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ borderRadius: 2 }}
              >
                {voiceText}
              </Alert>
            )}

            <Alert
              title="info"
              severity="info"
              sx={{ borderRadius: 2, bgcolor: 'common.white', border: (theme) => `1px solid ${theme.palette.divider}` }}
            >
              <b>{note}:</b> {startedNote}
            </Alert>
          </Stack>
        </Container>
      </Page>
    </>
  );
}
